// Typed calls to the customer app's API (same site, so the login cookie goes along automatically).

export type Status = 'driving' | 'idling' | 'stopped' | 'offline' | 'no-data';

export interface User {
  id: number;
  email: string;
  name: string;
  role: 'customer';
  customerId: number;
  customerName: string;
  mustChangePassword: boolean;
  // Set when a staff member opened the app as this user from Sprout.
  impersonatedBy: { id: number; name: string } | null;
}

export interface Vehicle {
  id: number;
  name: string;
  registration: string | null;
  type: VehicleType;
  customer: { id: number; name: string } | null;
  status: Status;
  since: string | null;
  lastReceived: string | null;
  position: {
    t: string;
    lat: number;
    lon: number;
    speedKmh: number | null;
    course: number | null;
    valid: boolean;
    satellites: number | null;
    powerV: number | null;
  } | null;
  // Country of the newest position (ISO code), null at sea or without a position.
  country: { code: string; name: string } | null;
  currentJourneyKm: number | null;
  todayKm: number | null;
  // Self-install units only: paid until and active / expiring / grace (about to be hidden).
  subscription: { paidUntil: string; status: 'active' | 'expiring' | 'grace' | 'expired'; hiddenFrom: string } | null;
  temperatures: CurrentTemperature[];
  // Fridge running or not, for vehicles with a "fridge" input (null otherwise).
  fridge: FridgeState | null;
}

export interface Place {
  lat: number;
  lon: number;
}

export interface Journey {
  start: string;
  end: string;
  open: boolean;
  endedBy: 'ignition' | 'stop' | 'gap' | 'open';
  durationMin: number;
  distanceKm: number;
  distanceSource: string;
  maxSpeedKmh: number | null;
  idleMin: number;
  // Only for vehicles with a mapped fuel counter; economy only from 10 km.
  fuelL: number | null;
  lPer100Km: number | null;
  from: Place;
  to: Place;
}

export interface Span extends Place {
  start: string;
  end: string | null;
  durationMin: number;
}

export interface Day {
  date: string;
  totalKm: number;
  totalFuelL: number | null;
  lPer100Km: number | null;
  journeys: Journey[];
  stops: Span[];
  idles: Span[];
}

// [time ms, lat, lon, speed km/h, course]
export type TrackPoint = [number, number, number, number | null, number | null];

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

// Told when the server says the login has ended (401), e.g. the login was disabled, its
// password reset or a "viewing as" session ran out, so the app can show the login screen.
// Not for requests where 401 means something else (wrong password, expired link…).
let sessionEnded: (() => void) | null = null;
export function onSessionEnded(fn: (() => void) | null): void {
  sessionEnded = fn;
}
const NOT_A_SESSION_CHECK = /^\/(login|impersonate|activate\/)/;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...init?.headers },
    credentials: 'same-origin',
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 401 && !NOT_A_SESSION_CHECK.test(path)) sessionEnded?.();
  if (!res.ok) throw new ApiError(res.status, body.error ?? `The server answered ${res.status}.`);
  return body as T;
}

export type VehicleType = 'truck' | 'van' | 'car' | 'trailer' | 'tractor' | 'plant' | 'other';

const json = (body: unknown) => ({ body: JSON.stringify(body) });

export const users = {
  changeOwnPassword: (current: string, password: string) =>
    request<{ ok: true }>('/me/password', { method: 'POST', ...json({ current, password }) }),
};

export const MIN_PASSWORD = 12;

export const api = {
  me: () => request<{ user: User }>('/me'),
  login: (email: string, password: string) =>
    request<{ user: User }>('/login', { method: 'POST', ...json({ email, password }) }),
  // Staff opening the app as this user from Sprout (one-time token from the link).
  viewAs: (token: string) => request<{ user: User }>('/impersonate', { method: 'POST', ...json({ token }) }),
  logout: () => request<{ ok: true }>('/logout', { method: 'POST' }),
  vehicles: () => request<{ vehicles: Vehicle[] }>('/vehicles'),
  // Self-install trackers on this account and how long they're paid for (incl. hidden ones).
  subscriptions: () => request<{ graceDays: number; vehicles: Array<{ id: number; name: string; registration: string | null; subscription: Vehicle['subscription'] }> }>('/subscriptions'),
  day: (id: number, date: string) => request<Day>(`/vehicles/${id}/day?date=${date}`),
  track: (id: number, from: string, to: string) =>
    request<{ points: TrackPoint[] }>(`/vehicles/${id}/track?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  temperatures: (id: number, from: string, to: string) =>
    request<{ sensors: TemperatureSeries[]; fridge: FridgeState | null }>(`/vehicles/${id}/temperatures?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  journey: (id: number, start: string) =>
    request<Journey & { track: TrackPoint[] }>(`/vehicles/${id}/journeys/${encodeURIComponent(start)}`),
};

// --- Self-install activation (public pages at /activate) ---------------------------------

export interface ActivateConfig {
  enabled: boolean;
  termsVersion: string;
  minPassword: number;
  years: number;
}

export interface Identified {
  ticket: string;
  tracker: { imei: string; model: string | null };
  orderEmail: string | null;
}

export interface NewAccount {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  password: string;
}

export interface NewVehicle {
  name: string;
  registration?: string;
  type: VehicleType;
  make?: string;
  model?: string;
}

export interface Signal {
  activatedAt: string | null;
  firstDataAt: string | null;
  lastReceived: string | null;
  position: { t: string; valid: boolean; lat: number; lon: number } | null;
}

// Errors from these calls carry a code (e.g. "already-activated", "locked", "email-taken").
export class ActivateError extends ApiError {
  constructor(status: number, message: string, public code: string | undefined) {
    super(status, message);
  }
}

async function activateCall<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/activate${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ActivateError(res.status, data.error ?? `The server answered ${res.status}.`, data.code);
  return data as T;
}

export const activate = {
  config: () => activateCall<ActivateConfig>('/config'),
  byToken: (token: string) => activateCall<Identified>('/token', { token }),
  byCode: (imei: string, claimCode: string) => activateCall<Identified>('/code', { imei, claimCode }),
  ticket: (ticket: string) => activateCall<Omit<Identified, 'ticket'> & { activated: boolean }>('/ticket', { ticket }),
  complete: (body: { ticket: string; acceptTerms: boolean; termsVersion: string; account?: NewAccount; vehicle: NewVehicle }) =>
    activateCall<{ vehicleId: number; expiresAt: string; newAccount: boolean }>('/complete', body),
  signal: (vehicleId: number) => activateCall<Signal>(`/signal/${vehicleId}`),
};

// Temperature inputs ("temperature:<name>" inputs) and fridge setpoints ("setpoint:<name>")
// with their newest reading.
export interface Limits { min?: number; max?: number }
export type TempKind = 'temperature' | 'setpoint';
export interface CurrentTemperature {
  kind: TempKind;
  name: string;
  unit: string;
  value: number | null;      // null: no reading (sensor fault or nothing sent lately)
  at: string | null;
  limits: Limits | null;
  state: 'ok' | 'low' | 'high' | 'none';
}
export interface TemperatureSeries {
  kind: TempKind;
  name: string;
  unit: string;
  limits: Limits | null;
  points: Array<[number, number]>;
  low: number | null;
  high: number | null;
  outside: number;
}
export interface FridgeState {
  on: boolean | null;       // null: no reading lately
  at: string | null;        // newest reading
  since: string | null;     // first reading of the current state (within 7 days)
}
// --- Geofences ---------------------------------------------------------------------------

export const GEOFENCE_COLOURS = ['blue', 'green', 'orange', 'purple', 'red', 'teal'] as const;
export type GeofenceColour = typeof GEOFENCE_COLOURS[number];

export interface Geofence {
  id: number;
  name: string;
  colour: GeofenceColour;
  customer: { id: number; name: string } | null;
  kind: 'circle' | 'polygon';
  circle: { lat: number; lon: number; radiusM: number } | null;
  points: Array<[number, number]>;  // [lon, lat], the outline (circles too)
  areaM2: number;
  insideNow: number;
  activeFrom: string;
  archivedAt: string | null;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface GeofenceEvent {
  kind: 'enter' | 'exit';
  at: string;
  passedThrough: boolean;
}

export interface VehicleGeofenceEvent extends GeofenceEvent {
  lastOtherSideAt: string | null;
  lat: number;
  lon: number;
  geofence: { id: number; name: string; colour: GeofenceColour; removed: boolean };
}

export interface GeofenceInput {
  name?: string;
  colour?: GeofenceColour;
  customerId?: number | null;
  circle?: { lat: number; lon: number; radiusM: number };
  points?: Array<[number, number]>;
}

export const geofences = {
  list: (q = '', customerId: number | 'none' | null = null) => request<{ geofences: Geofence[] }>(`/geofences?${new URLSearchParams({
    ...(q ? { q } : {}), ...(customerId !== null ? { customerId: String(customerId) } : {}) })}`),
  get: (id: number) => request<Geofence & { events: Array<GeofenceEvent & { vehicle: { id: number; name: string } }> }>(`/geofences/${id}`),
  add: (g: GeofenceInput) => request<{ id: number }>('/geofences', { method: 'POST', ...json(g) }),
  update: (id: number, g: GeofenceInput) => request<{ ok: true }>(`/geofences/${id}`, { method: 'PATCH', ...json(g) }),
  remove: (id: number) => request<{ ok: true }>(`/geofences/${id}/archive`, { method: 'POST' }),
  vehicleEvents: (vehicleId: number, from: string, to: string) =>
    request<{ events: VehicleGeofenceEvent[] }>(`/vehicles/${vehicleId}/geofence-events?${new URLSearchParams({ from, to })}`),
  searchPlaces: (q: string) => request<{ results: Array<{ label: string; lat: number; lon: number }> }>(`/geocode/search?${new URLSearchParams({ q })}`),
};
